/* src/views/logements/FicheLivretCtrl.jsx — L'ONGLET « Livret » de la fiche
   logement (lot 4, 2026-08-28) : la carte d'état, le renvoi Instructions,
   les QUATRE sections du proto (Le contenu · Automatique · L'allure · Les
   liens), la colonne « Ce que voit le voyageur », et les feuilles.

   ON NE PERD AUCUNE FONCTION :
   - Le contenu : catégories en cartes proto + modules réordonnables (dnd-kit,
     même PATCH reorder), édition par le LivretModuleEditor EXISTANT (3 langues,
     markdown/HTML, vidéo, conditions de visibilité, photo + cadrage), gestion
     de catégorie (renommer 3 langues + auto-traduire + supprimer gardée),
     pré-remplissage 9 appareils, copy-from add-only, FAQ complète
     (LivretFaqEditor monté tel quel).
   - Automatique : toggles + « Revoir » (régénération aux textes honnêtes par
     type, « Modifier à la main » = éditeurs existants complets).
   - L'allure : LivretConfigEditor EXISTANT monté tel quel (titre/sous-titre/
     slug + géocodage + carte draggable + météo + GetYourGuide) — le redessin
     proto de cette section est signalé en revue franche.
   - Les liens : site du logement (LivretInfosEditor), langues, et « Tout
     retraduire » qui porte LES DEUX passes : rattrapage des langues vides
     (jamais d'écrasement — la promesse écrite du proto) ET re-traduction
     totale destructive (modale de compte existante). */

import { useState, useMemo, useCallback } from 'react'
import { DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable,
  sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import { countOverwritableLangs } from '../../tabs/logements/LivretModulesEditor'
import LivretModuleEditor from '../../tabs/logements/LivretModuleEditor'
import LivretConfigEditor from '../../tabs/logements/LivretConfigEditor'
import LivretInfosEditor from '../../tabs/logements/LivretInfosEditor'
import LivretFaqEditor from '../../tabs/logements/LivretFaqEditor'
import LivretCopyFromModal from '../../tabs/logements/LivretCopyFromModal'
import CreateCategoryModal from '../../tabs/logements/CreateCategoryModal'
import RetranslateConfirmModal from '../../tabs/logements/RetranslateConfirmModal'
import LivretPreviewPanel from '../../tabs/logements/LivretPreviewPanel'
import LivretQrDownloadButton from '../../tabs/logements/LivretQrDownloadButton'
import Feuille from '../accueil/Feuille'
import { CarteLivretEtat, RenvoiInstructions, BasculeLivret, CadreApercuNote,
  ApercuLivretVoyageur, CarteAuto, CarteCategorie, ToggleRond } from './proto/LivretProto'
import { useLivretAdminData, enTetesAuth, joursDepuis, FeuilleDepublierLivret,
  FeuilleRefaireSection } from './livretPartage'

const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }

/* ── une ligne de module réordonnable (poignée + tag + chevron) ───── */
function LigneModule({ mod, premier, onOuvrir, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition,
    isDragging } = useSortable({ id: mod.id })
  return (
    <div ref={setNodeRef}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        background: '#fff', padding: '10px 12px', minHeight: '52px',
        borderTop: premier ? 'none' : '1px solid var(--bord)',
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.6 : 1, position: 'relative',
        zIndex: isDragging ? 5 : 'auto' }}>
      <span {...attributes} {...listeners} aria-label="Glisser pour réorganiser"
        style={{ cursor: 'grab', color: 'var(--gris-d)', fontSize: '13px',
          touchAction: 'none', flexShrink: 0 }}>⣿</span>
      <button onClick={onOuvrir} data-testid={`livret-module-${mod.id}`}
        style={{ flex: '1', minWidth: '0', textAlign: 'left', background: 'none',
          border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
          color: 'var(--navy-d)', padding: 0, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {mod.title?.fr || mod.title?.en || '(sans titre)'}</button>
      <button onClick={onToggle}
        className={`tag ${mod.enabled ? 't-v' : ''}`}
        title={mod.enabled ? 'Cliquer pour le retirer du livret' : 'Cliquer pour l’afficher'}
        style={{ cursor: 'pointer', border: 'none',
          ...(mod.enabled ? {} : { background: '#eef2f6', color: 'var(--gris)' }) }}>
        {mod.enabled ? 'affiché' : 'éteint'}</button>
      <button onClick={onOuvrir} aria-hidden="true" tabIndex={-1}
        style={{ fontSize: '14px', color: 'var(--gris-d)', flexShrink: '0',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>›</button>
    </div>
  )
}

export default function FicheLivretCtrl({ draft, onAllerInstructions }) {
  const { selected, c } = draft
  const log = selected.entity
  const livret = useLivretAdminData(log.id)
  const [section, setSection] = useState('contenu')
  const [feuille, setFeuille] = useState(null)
  // feuille ∈ null | 'depublier' | 'apercu-livret' | 'retraduire'
  //   | {regen} | {editModule} | {createIn} | {gererCat} | 'nouvelleCategorie'
  //   | {copyFrom: 'modules'|'faqs'}
  const [retradCount, setRetradCount] = useState(null)
  const [passeMsg, setPasseMsg] = useState(null)
  const [passeEnCours, setPasseEnCours] = useState(false)
  const [apercuReel, setApercuReel] = useState(null) // url iframe plein écran

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))

  const copyCandidates = useMemo(() => (getAllLogsFlat(c) || [])
    .filter((l) => l && l.id && l.id !== log.id)
    .map((l) => ({ id: l.id, nom: l.nom || l.id })), [c, log.id])

  // Modules groupés par catégorie (ordre = livret_categories, repli statique).
  const groupes = useMemo(() => {
    const ordre = livret.categories.length > 0
      ? livret.categories.map((cat) => cat.slug)
      : ['informations', 'guide_appareils', 'autre']
    const parCat = {}
    for (const slug of ordre) parCat[slug] = []
    for (const m of livret.modules) {
      const slug = m.category || 'autre'
      if (!parCat[slug]) parCat[slug] = []
      parCat[slug].push(m)
    }
    for (const slug of Object.keys(parCat)) {
      parCat[slug].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    }
    return { ordre: Object.keys(parCat), parCat }
  }, [livret.categories, livret.modules])

  const labelCat = (slug) => {
    const cat = livret.categories.find((x) => x.slug === slug)
    if (cat) return cat.label_fr || cat.label?.fr || slug
    return slug === 'guide_appareils' ? 'Guide des appareils'
      : slug === 'informations' ? 'Informations'
        : slug.charAt(0).toUpperCase() + slug.slice(1)
  }

  const surDragFin = useCallback(async (slug, event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const liste = groupes.parCat[slug] || []
    const ids = liste.map((m) => m.id)
    const nouvel = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id))
    try { await livret.modulesReorder(slug, nouvel) } catch (e) {
      console.error('[livret] reorder', e?.message || e)
    }
  }, [groupes, livret])

  const apercuReelOuvrir = useCallback(async () => {
    try {
      const headers = await enTetesAuth()
      const resp = await fetch('/api/beds24-mapping?action=livret_admin_preview_token', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'livret_admin_preview_token', logement_id: log.id }) })
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.message || json.error || `HTTP ${resp.status}`)
      setApercuReel(json.livret_url)
    } catch (e) {
      setPasseMsg(`Aperçu indisponible : ${e.message || e}`)
    }
  }, [log.id])

  // ── pas encore de livret ──────────────────────────────────────────
  if (livret.etat === 'no_config') {
    return (
      <div className="carte" style={{ textAlign: 'center', padding: '22px 14px' }}>
        <p style={{ fontSize: '12.5px', color: 'var(--navy-d)', fontWeight: 600,
          margin: '0 0 6px' }}>Aucun livret configuré</p>
        <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: '0 0 12px',
          lineHeight: 1.55 }}>Créez-le : le titre reprend le nom du logement,
          tout se règle ensuite.</p>
        <button className="bt bt-p" data-testid="livret-creer"
          onClick={() => livret.creerLivret(log.nom_complet || log.nom || log.id)}>
          Créer le livret</button>
      </div>
    )
  }
  if (livret.etat === 'error') {
    return (
      <div className="carte">
        <div className="info" style={{ borderColor: '#e8c4b8', background: '#fbeee9',
          color: '#8a3b28' }}>Livret illisible : {livret.erreur}</div>
        <button className="bt bt-s" onClick={livret.recharger}>Réessayer</button>
      </div>
    )
  }

  const publie = livret.config?.published === true
  const nbModulesActifs = livret.modulesAffiches.length
  const wifiLigne = Array.isArray(livret.wifiNetworks) && livret.wifiNetworks[0]?.ssid
    ? `${livret.wifiNetworks[0].ssid}${livret.wifiNetworks[0].password ? ` · ${livret.wifiNetworks[0].password}` : ''}`
    : null

  const colonneApercu = (
    <div className="surOrdi">
      <div style={{ position: 'sticky', top: '14px' }}>
        <p className="inter" style={{ marginTop: '0' }}>Ce que voit le voyageur</p>
        <CadreApercuNote />
        <ApercuLivretVoyageur titre={livret.config?.title?.fr || log.nom}
          wifi={wifiLigne}
          modules={livret.modulesAffiches.map((m) => m.title?.fr || '—')}
          alentoursLigne={livret.alentoursLigne}
          nbFaq={livret.faqs.length}
          guideVide={livret.guideVide} />
        <div className="act" style={{ marginTop: '9px' }}>
          <button className="bt bt-s" data-testid="livret-apercu-reel"
            onClick={apercuReelOuvrir}>Aperçu réel</button>
          <LivretQrDownloadButton slug={livret.config?.slug}
            title={livret.config?.title?.fr || log.nom} logement_id={log.id} />
          {publie && (
            <button className="bt bt-s" data-testid="livret-depublier"
              onClick={() => setFeuille('depublier')}>Dépublier</button>
          )}
          {!publie && (
            <button className="bt bt-p" data-testid="livret-publier"
              onClick={async () => {
                // Gardes du LivretPublishToggle : titre FR bloquant, avertissements groupés.
                if (!(livret.config?.title?.fr || '').trim()) {
                  window.alert('Le titre FR du livret est obligatoire avant publication (section L’allure).')
                  return
                }
                const avert = []
                if (nbModulesActifs === 0) avert.push('aucun module activé')
                const enManque = !livret.modules.some((m) => (m.title?.en || '').trim())
                if (livret.modules.length > 0 && enManque) avert.push('aucune traduction EN détectée')
                if (avert.length && !window.confirm(`Attention : ${avert.join(' · ')}.\n\nPublier quand même ?`)) return
                await livret.setPublished(true)
              }}>Publier le livret</button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <CarteLivretEtat publie={publie} nbModules={nbModulesActifs}
        nbQuestions={livret.faqs.length} />
      <div className="cols2">
        <div>
          <RenvoiInstructions onClick={onAllerInstructions} />
          <BasculeLivret actif={section} onSec={setSection}
            nbContenu={groupes.ordre.length}
            nbAuto={3} />

          {/* ═══ Le contenu ═══════════════════════════════════════ */}
          {section === 'contenu' && (
            <>
              {groupes.ordre.map((slug) => {
                const liste = groupes.parCat[slug]
                const systeme = livret.categories.find((x) => x.slug === slug)?.is_system
                return (
                  <CarteCategorie key={slug} label={labelCat(slug)} n={liste.length}
                    actif={liste.length > 0}
                    enTeteExtras={(
                      <button onClick={() => setFeuille({ gererCat: slug })}
                        aria-label={`Gérer la catégorie ${labelCat(slug)}`}
                        data-testid={`livret-cat-gerer-${slug}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '11px', flexShrink: 0,
                          color: liste.length > 0 ? 'rgba(255,255,255,.75)' : 'var(--gris-d)' }}>✏️</button>
                    )}
                    enfants={(
                      <>
                        {liste.length === 0 ? (
                          <div style={{ padding: '15px 12px', textAlign: 'center' }}>
                            <p style={{ fontSize: '11.5px', color: 'var(--gris)',
                              margin: '0 0 10px', lineHeight: '1.5' }}>
                              {slug === 'guide_appareils'
                                ? "Ce bloc n'apparaît pas au voyageur tant qu'il est vide."
                                : 'Rien ici pour l’instant.'}</p>
                            {slug === 'guide_appareils' ? (
                              <button className="bt bt-s" style={{ width: 'auto',
                                padding: '0 14px' }} data-testid="livret-prefill-appareils"
                                onClick={() => setFeuille('appareils')}>
                                Partir de neuf appareils courants</button>
                            ) : (
                              <button className="bt bt-s" style={{ width: 'auto',
                                padding: '0 14px' }}
                                onClick={() => setFeuille({ createIn: slug })}>＋ Ajouter</button>
                            )}
                          </div>
                        ) : (
                          <>
                            <DndContext sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(e) => surDragFin(slug, e)}>
                              <SortableContext items={liste.map((m) => m.id)}
                                strategy={verticalListSortingStrategy}>
                                {liste.map((m, i) => (
                                  <LigneModule key={m.id} mod={m} premier={i === 0}
                                    onOuvrir={() => setFeuille({ editModule: m })}
                                    onToggle={() => livret.moduleToggle(m.id, !m.enabled)} />
                                ))}
                              </SortableContext>
                            </DndContext>
                            <button onClick={() => setFeuille({ createIn: slug })}
                              data-testid={`livret-ajouter-${slug}`}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                width: '100%', background: '#fff', border: 'none',
                                borderTop: '1px solid var(--bord)', padding: '10px 12px',
                                minHeight: '44px', fontSize: '11.5px', color: 'var(--navy)',
                                cursor: 'pointer' }}>
                              <span style={{ fontSize: '14px' }}>＋</span>
                              Ajouter dans « {labelCat(slug)} »</button>
                          </>
                        )}
                      </>
                    )} />
                )
              })}
              <div className="act">
                <button className="bt bt-s" data-testid="livret-nouvelle-categorie"
                  onClick={() => setFeuille('nouvelleCategorie')}>＋ Catégorie</button>
                <button className="bt bt-s" data-testid="livret-copyfrom-modules"
                  onClick={() => setFeuille({ copyFrom: 'modules' })}>
                  Reprendre d'un autre logement</button>
              </div>

              <p className="inter">Les questions fréquentes</p>
              {/* FAQ complète (CRUD trilingue + FR→EN/ES) — composant existant
                  monté tel quel. */}
              <LivretFaqEditor logement_id={log.id} faqs={livret.faqs}
                onReload={livret.recharger} lang="fr" />
              <div className="act">
                <button className="bt bt-s" data-testid="livret-copyfrom-faqs"
                  onClick={() => setFeuille({ copyFrom: 'faqs' })}>
                  Reprendre des questions d'un autre logement</button>
              </div>
              <p className="note">Une question posée ici sert deux fois : elle
                s'affiche dans le livret, et l'opérateur s'en sert pour répondre.</p>
            </>
          )}

          {/* ═══ Automatique ══════════════════════════════════════ */}
          {section === 'auto' && (
            <>
              <div className="info">Trois blocs se construisent tout seuls à
                partir de l'adresse. Vous n'écrivez rien, mais vous décidez ce
                qui s'affiche.</div>
              {['assistance', 'alentours'].map((type) => {
                const row = livret.sectionsAuto?.[type]
                const jours = row?.last_generated_at ? joursDepuis(row.last_generated_at) : null
                return (
                  <CarteAuto key={type} icone="◷" ambre
                    testId={`livret-auto-${type}`}
                    titre={type === 'assistance' ? 'Assistance et urgences' : 'Aux alentours'}
                    sousTitre={type === 'assistance'
                      ? 'médecin, pharmacie et hôpital les plus proches'
                      : 'sept secteurs : commerces, transports, restaurants'}
                    on={row?.enabled === true}
                    toggleDisabled={!row || !row.last_generated_at}
                    onToggle={async () => {
                      try {
                        const headers = await enTetesAuth()
                        await fetch('/api/beds24-mapping?action=livret_sections_auto_toggle', {
                          method: 'POST', headers,
                          body: JSON.stringify({ action: 'livret_sections_auto_toggle',
                            logement_id: log.id, section_type: type,
                            enabled: !(row?.enabled === true) }) })
                        livret.recharger()
                      } catch (e) { console.error('[livret] toggle auto', e) }
                    }}
                    revuTexte={jours != null
                      ? `Revu il y a ${jours} jour${jours > 1 ? 's' : ''}. Un commerce qui ferme n'est pas détecté tout seul.`
                      : 'Jamais construit — « Revoir » le bâtit depuis l’adresse.'}
                    onRevoir={() => setFeuille({ regen: type })} />
                )
              })}
              {/* La météo n'a pas d'interrupteur propre : elle s'affiche dès
                  qu'une position est posée (L'allure). Texte = état actuel. */}
              <CarteAuto icone="✓"
                ambre={!(livret.config?.weather_lat != null && livret.config?.weather_lng != null)}
                testId="livret-auto-meteo"
                titre="La météo" sousTitre="les deux prochains jours"
                revuTexte={livret.config?.weather_lat != null && livret.config?.weather_lng != null
                  ? 'Elle suit la position posée dans L’allure.'
                  : 'Posez la position du logement (L’allure) pour l’afficher.'} />
              <p className="note">Ces blocs se refont à partir de l'adresse
                enregistrée dans les Instructions.</p>
            </>
          )}

          {/* ═══ L'allure ═════════════════════════════════════════ */}
          {section === 'allure' && (
            <>
              {/* Éditeur existant monté tel quel : titre/sous-titre/slug (les
                  anciens QR restent valides), géocodage + carte draggable,
                  ville météo, GetYourGuide. Redessin proto : à arbitrer à
                  l'écran (revue franche). */}
              <LivretConfigEditor config={livret.config} logement_id={log.id}
                onSave={livret.configSave} lang="fr" />
            </>
          )}

          {/* ═══ Les liens ════════════════════════════════════════ */}
          {section === 'liens' && (
            <>
              <LivretInfosEditor config={livret.config} logement_id={log.id}
                onReload={livret.recharger} />
              <div className="carte" style={{ margin: '9px 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                  padding: '9px 0' }}>
                  <span style={{ flex: '1', fontSize: '12px',
                    color: 'var(--gris)' }}>Mentions légales</span>
                  <b style={{ fontSize: '12.5px' }}>celles de FULLBNBZEN</b>
                </div>
                <p className="note">Ces liens s'affichent en pied du livret, sur
                  tous les logements.</p>
              </div>
              <p className="inter">Les langues</p>
              <div className="carte" style={{ margin: '0' }}>
                {[{ lb: 'Français', s: 'la source, celle que vous écrivez', tag: 'source', tv: true },
                  { lb: 'Anglais', s: 'traduit tout seul', tag: 'auto', tv: false },
                  { lb: 'Espagnol', s: 'traduit tout seul', tag: 'auto', tv: false }].map((l, i) => (
                  <div key={l.lb} style={{ display: 'flex', alignItems: 'center',
                    gap: '10px', padding: '9px 0',
                    borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%',
                      flexShrink: '0', background: l.tv ? '#2f9e6e' : '#87b8e6' }} />
                    <span style={{ flex: '1', minWidth: '0' }}>
                      <span style={{ display: 'block', fontSize: '12px',
                        fontWeight: '600', color: 'var(--navy-d)' }}>{l.lb}</span>
                      <span style={{ display: 'block', fontSize: '10px',
                        color: 'var(--gris)', marginTop: '1px' }}>{l.s}</span>
                    </span>
                    <span className={`tag ${l.tv ? 't-v' : 't-b'}`}>{l.tag}</span>
                  </div>
                ))}
                <div className="act">
                  <button className="bt bt-s" data-testid="livret-retraduire"
                    disabled={passeEnCours}
                    onClick={() => setFeuille('retraduire')}>
                    {passeEnCours ? 'Traduction…' : 'Tout retraduire'}</button>
                </div>
                {passeMsg && (
                  <p style={noteStyle} data-testid="livret-passe-msg">{passeMsg}</p>
                )}
                <p className="note">Une phrase que vous corrigez à la main en
                  anglais ou en espagnol n'est jamais écrasée — sauf si vous
                  choisissez explicitement d'écraser dans la feuille.</p>
              </div>
            </>
          )}
        </div>
        {colonneApercu}
      </div>

      {/* ── les feuilles / modales ─────────────────────────────────── */}
      {feuille === 'depublier' && (
        <FeuilleDepublierLivret
          onDepublier={async () => { await livret.setPublished(false); setFeuille(null) }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille && feuille.regen && (
        <FeuilleRefaireSection type={feuille.regen} logementId={log.id}
          sectionRow={livret.sectionsAuto?.[feuille.regen]}
          onFini={() => { livret.recharger(); setFeuille(null) }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille && (feuille.editModule || feuille.createIn) && (
        <LivretModuleEditor
          module={feuille.editModule || null}
          category={feuille.createIn || feuille.editModule?.category}
          logement_id={log.id}
          onSave={livret.moduleSave}
          onClose={() => { setFeuille(null); livret.recharger() }}
          currentPhoto={livret.modulePhotos[feuille.editModule?.id]?.[0] ?? null}
          lang="fr"
          onLangChange={() => {}} />
      )}
      {feuille && feuille.editModule && (
        <SupprimerModuleBouton mod={feuille.editModule}
          onDelete={async () => {
            const fait = await livret.moduleDelete(feuille.editModule)
            if (fait) setFeuille(null)
          }} />
      )}
      {feuille === 'nouvelleCategorie' && (
        <CreateCategoryModal
          existingSlugs={livret.categories.map((cat) => cat.slug)}
          onClose={() => setFeuille(null)}
          onSubmit={async (payload) => {
            await livret.creerCategorie(payload)
            setFeuille(null)
          }} />
      )}
      {feuille && feuille.gererCat && (
        <FeuilleGererCategorie slug={feuille.gererCat}
          categorie={livret.categories.find((x) => x.slug === feuille.gererCat)}
          nbModules={(groupes.parCat[feuille.gererCat] || []).length}
          logementId={log.id}
          onFini={() => { livret.recharger(); setFeuille(null) }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille && feuille.copyFrom && (
        <LivretCopyFromModal mode={feuille.copyFrom} targetLogementId={log.id}
          candidates={copyCandidates}
          onClose={() => setFeuille(null)}
          onCopied={async (res) => {
            const n = feuille.copyFrom === 'modules' ? res?.copied_modules : res?.copied_faqs
            const skipped = res?.skipped_photos
              ? ` (${res.skipped_photos} module(s) avec photos non copiées)` : ''
            setPasseMsg(`✅ ${n || 0} élément(s) copié(s)${skipped}.`)
            await livret.recharger()
            setFeuille(null)
          }} />
      )}
      {feuille === 'appareils' && (
        <Feuille titre="Partir de neuf appareils courants" testId="feuille-appareils"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">Neuf fiches se créent d'un coup, avec un
                texte à compléter. <b>Rien n'est écrasé</b> : ce qui existe
                déjà reste.</div>
              <div className="act">
                <button className="bt bt-p" data-testid="appareils-creer"
                  onClick={async () => {
                    try { await livret.prefillAppareils(); setFeuille(null) } catch (e) {
                      setPasseMsg(`Pré-remplissage échoué : ${e.message || e}`)
                    }
                  }}>Créer les neuf fiches</button>
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Annuler</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'retraduire' && (
        <Feuille titre="Tout retraduire" testId="feuille-retraduire"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <p style={{ fontSize: '12px', color: 'var(--navy-d)',
                lineHeight: '1.55', margin: '0 0 10px' }}>Deux passes possibles —
                la première ne touche jamais à ce qui existe.</p>
              <div className="act" style={{ flexDirection: 'column',
                alignItems: 'stretch' }}>
                <button className="bt bt-p" data-testid="retraduire-rattraper"
                  onClick={async () => {
                    setFeuille(null); setPasseEnCours(true); setPasseMsg(null)
                    try {
                      const r = await livret.passeTraduction({ overwrite: false })
                      setPasseMsg(`✅ ${r.translated} traduit(s), ${r.complete} déjà complet(s).`)
                    } catch (e) {
                      setPasseMsg(`⚠️ Rattrapage interrompu : ${e.message || e}`)
                    } finally { setPasseEnCours(false) }
                  }}>Rattraper les langues vides (recommandé)</button>
                <button className="bt bt-s" data-testid="retraduire-ecraser"
                  style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                  onClick={() => {
                    setFeuille(null)
                    setRetradCount(countOverwritableLangs(livret.modules, livret.faqs, livret.config))
                  }}>Écraser et re-traduire tout…</button>
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Annuler</button>
              </div>
              <p className="note">Le rattrapage remplit uniquement l'anglais et
                l'espagnol vides. L'écrasement re-traduit tout depuis le
                français, y compris vos corrections — il se confirme.</p>
            </>
          )} />
      )}
      {retradCount !== null && (
        <RetranslateConfirmModal count={retradCount}
          onClose={() => setRetradCount(null)}
          onConfirm={async () => {
            setRetradCount(null); setPasseEnCours(true); setPasseMsg(null)
            try {
              const r = await livret.passeTraduction({ overwrite: true })
              setPasseMsg(`⚠️ ${r.translated} re-traduits, ${r.complete} inchangés, ${r.failures} échec(s) — les versions existantes des éléments en échec sont conservées.`)
            } finally { setPasseEnCours(false) }
          }} />
      )}
      {feuille === 'apercu-livret' && null}
      {apercuReel && (
        <LivretPreviewPanel previewUrl={apercuReel}
          onClose={() => setApercuReel(null)}
          onReload={apercuReelOuvrir} />
      )}
    </>
  )
}

/* Le bouton « Supprimer » accessible pendant l'édition d'un module — le
   LivretModuleEditor existant n'en porte pas (il vit sur la carte) : on le
   pose en pied de page flottant, même confirm irréversible. */
function SupprimerModuleBouton({ mod, onDelete }) {
  return (
    <div style={{ position: 'fixed', bottom: '14px', right: '14px', zIndex: 10001 }}>
      <button className="bt bt-s" data-testid="module-supprimer"
        style={{ color: '#b8412b', borderColor: '#e8c4b8',
          boxShadow: '0 4px 14px rgba(35,50,64,.25)', background: '#fff' }}
        onClick={onDelete}>Supprimer ce module…</button>
    </div>
  )
}

/* ── la feuille « Gérer la catégorie » (renommer 3 langues · traduire ·
   supprimer gardée) — handlers portés de LivretModulesEditor/CategoryColumn. */
function FeuilleGererCategorie({ slug, categorie, nbModules, logementId, onFini, onFermer }) {
  const [labelFr, setLabelFr] = useState(categorie?.label_fr || categorie?.label?.fr || '')
  const [labelEn, setLabelEn] = useState(categorie?.label_en || categorie?.label?.en || '')
  const [labelEs, setLabelEs] = useState(categorie?.label_es || categorie?.label?.es || '')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState(null)
  const systeme = categorie?.is_system === true

  const inp = (value, onChange, testId) => (
    <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={60}
      data-testid={testId}
      style={{ width: '100%', minHeight: '38px', padding: '0 12px',
        borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
        fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none',
        boxSizing: 'border-box', marginBottom: '7px' }} />
  )

  const traduire = async () => {
    if (!labelFr.trim()) return
    setEnCours(true)
    try {
      const headers = await enTetesAuth()
      const [enR, esR] = await Promise.allSettled(['en', 'es'].map((loc) =>
        fetch('/api/beds24-mapping?action=livret_translate', {
          method: 'POST', headers,
          body: JSON.stringify({ action: 'livret_translate', source_text: labelFr,
            target_locale: loc, context: 'category_label' }) }).then((r) => r.json())))
      if (enR.status === 'fulfilled' && !enR.value.fallback && enR.value.translated) setLabelEn(enR.value.translated)
      if (esR.status === 'fulfilled' && !esR.value.fallback && esR.value.translated) setLabelEs(esR.value.translated)
    } finally { setEnCours(false) }
  }

  const enregistrer = async () => {
    if (!categorie || !labelFr.trim()) { onFermer(); return }
    setEnCours(true); setErreur(null)
    try {
      const headers = await enTetesAuth()
      const resp = await fetch('/api/beds24-mapping?action=livret_category_update', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'livret_category_update', id: categorie.id,
          logement_id: logementId, label_fr: labelFr, label_en: labelEn,
          label_es: labelEs }) })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(json.message || json.error || `HTTP ${resp.status}`)
      onFini()
    } catch (e) { setErreur(e.message || String(e)); setEnCours(false) }
  }

  const supprimer = async () => {
    if (nbModules > 0) {
      window.alert(`${nbModules} module(s) vivent encore dans cette catégorie — déplacez-les ou supprimez-les d'abord.`)
      return
    }
    if (!window.confirm(`Supprimer la catégorie « ${labelFr || slug} » ? Cette action est irréversible.`)) return
    setEnCours(true); setErreur(null)
    try {
      const headers = await enTetesAuth()
      const resp = await fetch('/api/beds24-mapping?action=livret_category_delete', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'livret_category_delete', id: categorie.id,
          logement_id: logementId }) })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        if (json.error === 'delete_blocked_modules_present') {
          throw new Error('Des modules vivent encore dans cette catégorie.')
        }
        throw new Error(json.message || json.error || `HTTP ${resp.status}`)
      }
      onFini()
    } catch (e) { setErreur(e.message || String(e)); setEnCours(false) }
  }

  if (!categorie) {
    return (
      <Feuille titre="Cette catégorie" testId="feuille-gerer-categorie"
        onFermer={onFermer}
        enfants={(
          <>
            <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: 0,
              lineHeight: 1.55 }}>Catégorie standard du livret — son nom ne se
              change pas ici.</p>
            <div className="act">
              <button className="bt bt-s" onClick={onFermer}>Fermer</button>
            </div>
          </>
        )} />
    )
  }

  return (
    <Feuille titre="Gérer la catégorie" suffixe={slug}
      testId="feuille-gerer-categorie" onFermer={onFermer}
      enfants={(
        <>
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 4px' }}>Nom (français)</p>
          {inp(labelFr, setLabelFr, 'cat-label-fr')}
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 4px' }}>Anglais</p>
          {inp(labelEn, setLabelEn, 'cat-label-en')}
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 4px' }}>Espagnol</p>
          {inp(labelEs, setLabelEs, 'cat-label-es')}
          <button className="bt bt-s" style={{ marginBottom: '10px' }}
            disabled={enCours || !labelFr.trim()} onClick={traduire}>
            Auto-traduire EN/ES</button>
          {erreur && (
            <div className="info" style={{ borderColor: '#e8c4b8',
              background: '#fbeee9', color: '#8a3b28' }}>{erreur}</div>
          )}
          {systeme ? (
            <p style={noteStyle}>Catégorie système — elle ne peut pas être supprimée.</p>
          ) : (
            <button className="bt bt-s" data-testid="cat-supprimer"
              style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
              disabled={enCours} onClick={supprimer}>Supprimer la catégorie…</button>
          )}
          <div className="act">
            <button className="bt bt-p" data-testid="cat-enregistrer"
              disabled={enCours || !labelFr.trim()}
              onClick={enregistrer}>Enregistrer</button>
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
        </>
      )} />
  )
}
